import { useEffect, useRef, useCallback, useState } from "react";
import {
  FieldValues,
  UseFormWatch,
  UseFormSetError,
  UseFormClearErrors,
  Path,
} from "react-hook-form";

import { useCartMutations } from "../hooks/useCartMutations";
import type { CheckoutFormData } from "../checkout.types";
import { Address } from "@/networking/addresses.api";
import { validateUsZipCode } from "./useGoogleAddressValidation";
import { useTranslation } from "@/hooks/useTranslation";

function useAddressSync(
  field: FieldValues,
  cartToken: string,
  paymentMethodsIncludes: string | null,
  watch: UseFormWatch<CheckoutFormData>,
  type: "ship_to" | "bill_to",
  setError?: UseFormSetError<CheckoutFormData>,
  clearErrors?: UseFormClearErrors<CheckoutFormData>,
) {
  const { t } = useTranslation();
  const debounceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSentData = useRef<string>("");
  const lastCheckboxValue = useRef<boolean | undefined>(undefined);
  const isInitialized = useRef(false);
  const [zipValidationPending, setZipValidationPending] = useState(false);

  const { updateAddressMutation } = useCartMutations(
    cartToken,
    paymentMethodsIncludes,
  );

  // Watch the entire address object instead of individual fields
  const watchedAddress = watch(field.name);

  const shippingAddress = watch("customer.shipping_address");

  // watch the checkbox value for billing addresses
  const useShippingForBilling = watch(
    "customer.payment_method.use_shipping_address_for_billing",
  );

  // credit card form name (used for bill_to when checkbox is unchecked)
  const paymentMethodFirstName = watch("customer.payment_method.first_name");
  const paymentMethodLastName = watch("customer.payment_method.last_name");

  const meaningfulData =
    watchedAddress?.country_code?.length > 0 &&
    watchedAddress?.address1?.length > 0;

  const postalCodeFieldPath =
    type === "ship_to"
      ? ("customer.shipping_address.postal_code" as Path<CheckoutFormData>)
      : ("customer.payment_method.billing_address.postal_code" as Path<CheckoutFormData>);

  const handleUpdateAddress = useCallback(
    async (type: "ship_to" | "bill_to") => {
      // Check if we have meaningful data
      if (!meaningfulData) {
        return;
      }

      // Validate US zip codes before syncing
      if (
        watchedAddress?.country_code === "US" &&
        watchedAddress?.postal_code &&
        watchedAddress?.state &&
        watchedAddress?.address1 &&
        watchedAddress?.city &&
        setError &&
        clearErrors
      ) {
        setZipValidationPending(true);
        try {
          const validationResult = await validateUsZipCode({
            address1: watchedAddress.address1,
            address2: watchedAddress.address2,
            city: watchedAddress.city,
            state: watchedAddress.state,
            postal_code: watchedAddress.postal_code,
          });

          if (!validationResult.isValid) {
            setError(postalCodeFieldPath, {
              type: "warning",
              message: t("zip_code_invalid_for_state"),
            });
          } else {
            clearErrors(postalCodeFieldPath);
          }
        } catch {
          // On validation error, allow the request to proceed
          console.error("Zip code validation failed, proceeding with sync");
        } finally {
          setZipValidationPending(false);
        }
      } else if (watchedAddress?.country_code !== "US" && clearErrors) {
        // Clear any previous zip validation error when switching to non-US country
        clearErrors(postalCodeFieldPath);
      }

      const effectiveType: "ship_to" | "bill_to" | "both" =
        type === "ship_to" && useShippingForBilling ? "both" : type;

      // for bill_to: when checkbox checked use shipping name; when unchecked use credit card form name (billing form has collectName={false})
      const addressName =
        type === "bill_to"
          ? useShippingForBilling
            ? shippingAddress?.name || watchedAddress.name
            : `${paymentMethodFirstName || ""} ${paymentMethodLastName || ""}`.trim() ||
              shippingAddress?.name ||
              watchedAddress.name
          : watchedAddress.name;

      const addressBody: Address = {
        address: {
          name: addressName,
          address1: watchedAddress.address1,
          address2: watchedAddress.address2 ?? "",
          city: watchedAddress.city,
          state: watchedAddress.state,
          postal_code: watchedAddress.postal_code,
          country_code: watchedAddress.country_code,
        },
        type: effectiveType,
      };

      addressBody.use_shipping_address_for_billing =
        useShippingForBilling ?? false;

      updateAddressMutation.mutate(addressBody);

      // Update last sent data
      lastSentData.current = JSON.stringify(watchedAddress);
    },
    [
      watchedAddress,
      shippingAddress,
      paymentMethodFirstName,
      paymentMethodLastName,
      updateAddressMutation,
      meaningfulData,
      useShippingForBilling,
      setError,
      clearErrors,
      postalCodeFieldPath,
      t,
    ],
  );

  useEffect(() => {
    const currentDataString = JSON.stringify(watchedAddress);

    // Initialize on first run (don't send API call)
    if (!isInitialized.current) {
      lastSentData.current = currentDataString;
      lastCheckboxValue.current = useShippingForBilling;
      isInitialized.current = true;
      return;
    }

    // Check if data actually changed or checkbox value changed
    const addressChanged = currentDataString !== lastSentData.current;
    const checkboxChanged =
      lastCheckboxValue.current !== undefined &&
      lastCheckboxValue.current !== useShippingForBilling;

    if (!addressChanged && !checkboxChanged) {
      return;
    }

    // Clear existing timeout
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    // Set new timeout
    debounceTimeout.current = setTimeout(() => {
      handleUpdateAddress(type);
      lastCheckboxValue.current = useShippingForBilling;
    }, 500);

    // Cleanup function
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, [watchedAddress, handleUpdateAddress, type, useShippingForBilling]);

  return {
    addressSyncPending: updateAddressMutation.isPending || zipValidationPending,
  };
}

export default useAddressSync;
